import React, { useEffect, useRef, useState } from "react";
import styles from "./Tabs.module.css";

export type TabItem = {
  key: string|number;
  label: React.ReactElement | string;
  children?: React.ReactElement,
  disabled?: boolean,
};

type TabsProps = {
  tabs: TabItem[];
  tabItemClassName?: string,
  activeTab: string|number;
  setActiveTab: (value: string|number) => void;
  shrink?: boolean;
  noBorder?: boolean;
  stretchTabsToCover?: boolean;
};

function Tabs({
  tabs,
  activeTab,
  setActiveTab,
  shrink = false,
  noBorder = false,
  tabItemClassName,
  stretchTabsToCover = false,
}: TabsProps) {
  const tabRefs = useRef<{ [key: string]: HTMLDivElement }>({});
  const tabItem = activeTab == null ? null : tabs.find(tab => tab.key === activeTab);
  const [highlightPos, setHighlightPos] = useState<{left?: string, width?: string}>({});
  
  useEffect(() => {
    requestAnimationFrame(() => {
      setHighlightPos({
        left: `${tabRefs.current[activeTab]?.offsetLeft}px`,
        width: `${tabRefs.current[activeTab]?.offsetWidth}px`,
      });
    });
  }, [tabRefs.current[activeTab]]);

  return (
    <div className={styles.tabsContainer} style={{maxHeight: shrink ? "100%" : ""}}>
      <div className={styles.tabItemsContainer} style={{border: noBorder ? "none" : ""}}>
        {tabs.map(t => (
          <div
            key={t.key}
            className={`
              ${styles.tabItem}
              ${tabItemClassName || ""}
              ${activeTab === t.key ? styles.tabItemActive : ""}
              ${!!t.disabled ? styles.tabItemDisabled : ""}
            `}
            style={{ flex: stretchTabsToCover ? "1" : "unset" }}
            ref={(el) => {
              if (el) tabRefs.current[t.key] = el;
            }}
            onClick={() => !t.disabled && setActiveTab(t.key)}
          >
            {t.label}
          </div>
        ))}

        {activeTab != null && <div
          className={styles.highlight}
          style={highlightPos}
        />}
      </div>

      {!!tabItem?.children && (
        <div className={styles.tabChildrenContainer} style={{minHeight: shrink ? 0 : ""}}>
          {tabItem.children}
        </div>
      )}
    </div>
  );
};

export default Tabs;